<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Visitors"
      subtitle="Track and manage all society visitors"
      :columns="columns"
      :items="visitors"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Header Extra: Multi-Filters & Export -->
      <template #header-extra>
        <div class="header-filters">
          <!-- Date Range Picker -->
          <PremiumDatePicker
            v-model="dateRange"
            range
            placeholder="Select Date Range"
            @change="onDateChange"
          />

          <!-- Block Filter -->
          <FilterDropdown
            v-if="useBlocks"
            v-model="selected_flat"
            :options="flats"
            item-text="name"
            item-value="id"
            placeholder="Block / Category"
            all-label="All"
            @change="
              if (page === 1) {
                getVisitors();
              } else {
                page = 1;
              }
              getHouseNumber();
            "
          />

          <!-- House Filter -->
          <FilterDropdown
            v-model="house_number"
            :options="houseNumber"
            item-text="house_number"
            item-value="id"
            placeholder="House / Unit"
            all-label="All"
            @change="if (page === 1) getVisitors(); else page = 1;"
          />

          <!-- Export Button -->
          <button
            class="pt-export-btn"
            :style="{ background: brandColor }"
            title="Export to Excel"
            @click="exportDialog = true"
          >
            <v-icon size="16" color="white">mdi-download-outline</v-icon>
          </button>
        </div>
      </template>

      <!-- Custom Cells -->
      <template #col-profile_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img
              v-if="item.profile_url && item.profile_url !== 'null'"
              :src="API_BASE_URL + '/uploads/visitors/' + item.profile_url"
              class="p-avatar-img"
              style="cursor: pointer"
              @click.stop="openPhoto(API_BASE_URL + '/uploads/visitors/' + item.profile_url, item.name)"
            />
            <div
              v-else
              class="p-avatar-placeholder"
              :style="{ background: primaryColor + '20', color: primaryColor }"
            >
              {{
                item.name && item.name !== "null"
                  ? item.name.charAt(0).toUpperCase()
                  : "V"
              }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{
              item.name && item.name !== "null" ? item.name : "—"
            }}</span>
            <span
              class="p-id"
              v-if="item.mobile_no"
              style="display: flex; align-items: center; gap: 4px"
            >
              <v-icon size="12" color="#94A3B8">mdi-phone-outline</v-icon>
              {{
                item.country_code
                  ? (item.country_code.toString().startsWith("+")
                      ? item.country_code
                      : "+" + item.country_code) + " "
                  : ""
              }}{{ item.mobile_no }}
            </span>
          </div>
        </div>
      </template>

      <template #col-flat_id="{ item }">
        <div class="house-number-badge">
          <v-icon size="14"  color="#94A3B8" class="mr-1">mdi-home-outline</v-icon>
          <span v-if="item.flat_no" class="flat-prefix">{{ item.flat_no }}-</span>
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <template #col-number_of_visitors="{ item }">
        <div class="visitor-count-pill">
          <i class="mdi mdi-account-multiple-outline visitor-pill-icon mr-1"></i>
          <span>{{ item.number_of_visitors || 1 }}</span>
        </div>
      </template>

      <template #col-vehicle_no="{ item }">
        <span class="vehicle-text" v-if="item.vehicle_no">
          <v-icon size="14" color="#64748B" class="mr-1"
            >mdi-car-outline</v-icon
          >
          {{ item.vehicle_no }}
        </span>
        <span class="dash" v-else>—</span>
      </template>

      <template #col-purpose="{ item }">
        <span
          v-if="item.purpose && item.purpose !== 'null'"
          class="purpose-pill"
        >
          {{ item.purpose }}
        </span>
        <span v-else class="dash">—</span>
      </template>

      <template #col-visited_at="{ item }">
        <span class="date-text">{{ item.visited_at | formatDateTime }}</span>
      </template>
    </PremiumTable>

    <!-- ── Export Confirmation ──────────────── -->
    <ConfirmModal
      v-model="exportDialog"
      type="primary"
      :btnColor="brandColor"
      title="Export to Excel"
      message="Are you sure you want to export the visitor log to an Excel file?"
      confirm-text="Yes, Export"
      @confirm="
        exportToExcel();
        exportDialog = false;
      "
    />

    <!-- ── Media Viewer ────────────────────── -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState, mapGetters } from "vuex";
import moment from "moment";
import MediaViewer from "../../common/MediaViewer.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "VisitorList",
  mixins: [filterMixin],
  components: { PremiumTable, PremiumDatePicker, ConfirmModal, FilterDropdown, MediaViewer },

  data() {
    return {
      search: "",
      visitors: [],
      totalRecords: 0,
      loading: false,
      page: 1,
      itemsPerPage: 10,
      selected_flat: "",
      house_number: "",
      flats: [],
      houseNumber: [],
      startDate: moment().format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      dateRange: [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      user: {},
      exportDialog: false,
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    ...mapGetters("society", ["useBlocks", "unitType"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        {
          key: "profile_url",
          label: this.$t("visitor.headers.name") || "Visitor",
          width: "260px",
        },
        {
          key: "flat_id",
          label: this.$t("visitor.headers.house_no") || "Visiting Unit",
          width: "140px",
        },
        { key: "number_of_visitors", label: "Count", width: "80px" },
        { key: "purpose", label: "Purpose", width: "160px", hideOnMobile: true },
        { key: "vehicle_no", label: "Vehicle", width: "160px", hideOnTablet: true },
        {
          key: "visited_at",
          label: this.$t("visitor.headers.visited_at") || "Check-In",
          width: "180px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getVisitors();
    },
    itemsPerPage() {
      this.getVisitors();
    },
    search() {
      if (this.page === 1) {
        this.getVisitors();
      } else {
        this.page = 1;
      }
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getVisitors();
    },

    onDateChange(val) {
      if (val && val.length === 2) {
        const sorted = [...val].sort();
        this.startDate = sorted[0];
        this.endDate = sorted[1];
      } else if (val && val.length === 1) {
        this.startDate = val[0];
        this.endDate = val[0];
      } else {
        this.startDate = "";
        this.endDate = "";
      }
      if (this.page === 1) {
        this.getVisitors();
      } else {
        this.page = 1;
      }
    },

    getVisitors() {
      this.loading = true;
      const sortBy = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      let flat_id = this.selected_flat;
      let unit_type = "";
      if (flat_id === "all_shops") {
        flat_id = "";
        unit_type = 2;
      }

      this.$axios
        .get("societyadmin/visitors", {
          params: {
            society_id: this.user.id,
            flat_id: flat_id || "",
            unit_type: unit_type,
            house_number: this.house_number,
            page: this.page,
            perPage: this.itemsPerPage,
            sortBy,
            sortDesc,
            search: this.search,
            startDate: this.startDate,
            endDate: this.endDate,
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            // Priority: data.visitors.data (paginated) > data.visitors (array)
            this.visitors = data.visitors.data || data.visitors || [];
            
            // Priority: data.visitors.total (nested) > data.total (top-level)
            this.totalRecords = (data.visitors && data.visitors.total) || data.total || 0;
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    exportToExcel() {
      this.loading = true;
      this.$axios
        .get("societyadmin/export/visitors", {
          params: {
            society_id: this.user.id,
            flat_id: this.selected_flat,
            house_number: this.house_number,
            sortBy: this.options.sortBy[0],
            sortDesc: this.options.sortDesc[0],
            search: this.search,
            startDate: this.startDate,
            endDate: this.endDate,
          },
          responseType: "blob",
        })
        .then((response) => {
          this.loading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Visitors_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.loading = false;
          this.showError("Failed to export data");
        });
    },

    getFlats() {
      this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },

    getHouseNumber() {
      let flat_id = this.selected_flat;
      let unit_type = "";
      if (flat_id === "all_shops") {
        flat_id = "";
        unit_type = 2;
      }
      this.$axios
        .get("societyadmin/houseNumber", {
          params: {
            flat_id: flat_id || "",
            unit_type: unit_type,
          },
        })
        .then((response) => {
          const data = response.data;
          if (data.status) {
            this.houseNumber = data.houses;
          }
        });
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },

    getStatusStyles() {
      return {
        background: "rgba(16, 185, 129, 0.1)",
        color: "#10B981",
        border: "1px solid rgba(16, 185, 129, 0.2)",
      };
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },

    debounceSearch: debounce(function () {
      this.page = 1;
      this.getVisitors();
    }, 500),
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    this.initFilters("visitor");

    this.getFlats();
    this.getVisitors();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.icon-action-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tx2);
  cursor: pointer;
  transition: all 0.2s;
}
.icon-action-btn:hover {
  border-color: var(--brand);
  color: var(--brand);
  background: var(--surface2);
}

/* ── Filters ── */
.header-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* ← Add these two rules */
.header-filters ::v-deep .premium-date-wrap {
  width: auto;
  flex-shrink: 0;
}

.header-filters ::v-deep .premium-date-trigger {
  min-width: 220px;
  height: 38px;
  background: var(--surface2, #f8fafc);
}

@media (max-width: 960px) {
  .header-filters {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px !important;
    width: 100% !important;
  }
  .header-filters .premium-date-wrap {
    width: 100% !important;
  }
  .header-filters ::v-deep .premium-date-trigger {
    width: 100% !important;
    min-width: 0 !important;
  }
  .header-filters .fdd-wrap {
    width: 100% !important;
  }
  .header-filters ::v-deep .fdd-trigger {
    width: 100% !important;
    max-width: none !important;
  }
  .header-filters .pt-export-btn {
    width: 100% !important;
    height: 38px !important;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .header-filters .pt-export-btn::after {
    content: "Export to Excel";
    margin-left: 8px;
    color: white;
    font-size: 13px;
    font-weight: 600;
  }
}
/* ── Cells ── */
.profile-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.p-avatar-wrap {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  min-width: 40px;
  min-height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface2);
  border: 1px solid var(--border);
}
.p-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.p-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
}

.p-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.p-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx);
}
.p-id {
  font-size: 11px;
  font-weight: 500;
  color: var(--tx3);
}

.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}
.house-number-badge .flat-prefix {
  color: var(--brand);
  opacity: 0.8;
}
.house-number-badge .house-num {
  color: var(--brand);
}

.unit-cell {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}
.flat-name {
  color: var(--tx4);
}

.visitor-count-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 12px;
  border-radius: 20px;
  background: var(--surface2, #f8fafc);
  color: var(--brand);
  font-size: 12px;
  border: 1px solid var(--brand-lt);
}

/* Plain MDI icon — bypasses Vuetify theme color injection */
.visitor-pill-icon {
  font-size: 13px;
  color: inherit;
  line-height: 1;
  vertical-align: middle;
  flex-shrink: 0;
}

.vehicle-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--tx2);
  font-family: "JetBrains Mono", monospace;
}
.date-text {
  font-size: 12.5px;
  color: var(--tx3);
  font-weight: 500;
}
.dash {
  color: var(--tx3);
  font-size: 13px;
}
.purpose-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #f1f5f9);
  color: var(--tx2, #475569);
  font-size: 11.5px;
  font-weight: 600;
  border: 1px solid var(--border, #e2e8f0);
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Status Badge ── */
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
</style>
